'use client';

import { cn } from '@/lib/cn';
import {
  MESSAGE_TYPES,
  CONTENT_TYPES,
  parseMessageSegments,
  type MessageSegment,
} from '@repo/utils';
import type { Message } from '@repo/api';
import { IconIglooLogo } from '@repo/svgs';

interface MessageBubbleProps {
  message: Message;
  isGrouped?: boolean | undefined;
  onUrlClick?: (url: string) => void;
  children?: React.ReactNode;
}

const renderMessageWithLinks = (text: string, onUrlClick?: (url: string) => void) => {
  const segments = parseMessageSegments(text);
  if (segments.every(s => s.kind === 'text')) {
    return <span>{text}</span>;
  }
  return segments.map((seg: MessageSegment, i) => {
    if (seg.kind === 'link') {
      return (
        <a
          key={i}
          href={seg.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onUrlClick?.(seg.url)}
          className={cn(
            'underline underline-offset-2',
            'text-accent hover:text-accent/80',
            'transition-colors',
          )}
        >
          {seg.label}
        </a>
      );
    }
    return <span key={i}>{seg.value}</span>;
  });
};

export function MessageBubble({ message, isGrouped, onUrlClick, children }: MessageBubbleProps) {
  const isUser = message.senderType === MESSAGE_TYPES.USER;
  const isLoading = message.contentType === CONTENT_TYPES.LOADING;

  return (
    <div
      className={cn('flex items-start gap-2', isUser ? 'justify-end pl-21' : 'justify-start pr-12')}
    >
      {/* Non-user Avatar — invisible when grouped to keep bubble alignment */}
      {!isUser && (
        <div
          className={cn(
            'flex-shrink-0 w-8 h-8 rounded-full bg-secondary flex items-center justify-center overflow-hidden',
            isGrouped && 'invisible',
          )}
        >
          {message.senderImage ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={message.senderImage}
              alt={message.senderName ?? ''}
              className="w-5 h-5 rounded-full"
            />
          ) : (
            <IconIglooLogo className="w-4 h-auto self-center text-primary overflow-visible" />
          )}
        </div>
      )}

      {/* Message Bubble */}

      <div
        className={cn(
          'min-w-0 whitespace-pre-line break-words rounded-3xl px-3 py-2 text-dark-900',
          isUser ? 'bg-active' : 'bg-secondary',
        )}
      >
        {isLoading ? (
          <div className="flex h-full items-center justify-center gap-x-1">
            <span className="block h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
            <span className="block h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
            <span className="block h-2 w-2 animate-bounce rounded-full bg-gray-400" />
          </div>
        ) : (
          <p className="text-body-2 whitespace-pre-wrap text-primary">
            {renderMessageWithLinks((message.contentMessage as string) || '', onUrlClick)}
          </p>
        )}
      </div>
    </div>
  );
}
